import React, { useEffect, useContext, createContext, useState, useRef } from 'react'
import clsx from 'clsx'
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { makeStyles } from '@mui/styles'
import './App.css';
import CssBaseline from '@mui/material/CssBaseline';
import { BooleanParam, useQueryParam, JsonParam, withDefault } from 'use-query-params'
import TopBar from './components/top_bar'
import { SocketTable } from './components/socket_table'
import { socket, SocketContext } from './contexts/socket'
import { LatencyIndicator } from './components/latency_indicator';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { get_userinfo, get_schedule, get_schedule_by_user, UserInfo, ScheduleByUserEntry, ScheduleEntry, get_testSemids } from './api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import SemidDisplay from './components/semid_display';
import { ReportRequester } from './components/report_requester'

import apiClient from './utils/http-common';
import Home from './pages/Home/home';
import Gui from './pages/Gui/gui';
import FooterBar from './components/footer_bar'
import VersionInfo from './components/versioninfo'
import RequireAuth from './components/RequireAuth'

import { Container } from '@mui/material'

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useHistory,
  useLocation
} from "react-router-dom";

import Banner from './components/banner';
import Login from './components/user_login';
import Profile from './components/Profile';
import { useAuth } from './contexts/AuthContext';
import NotFound from './components/NotFound';
import { useCookies } from 'react-cookie';
import { watch } from 'fs';
import { replace } from 'lodash';
import Dashboard from './pages/Dashboard/dashboard';
import jwt_decode from 'jwt-decode';
import NewSessionPrompt from './components/modal_newsession';
import { logout_axios } from './services/auth-service';
import ReactGA from 'react-ga4';

export const MAX_WIDTH = '1600px'

ReactGA.initialize([{
   trackingId: 'G-HJCHVKFPZX',
   gaOptions:  {
     debug: true,
     titleCase: true,
     testMode: true,
   }
}])


export interface HeaderTable {
  title: string,
  user: string,
  semids: string,
  instrument: string,
  sa: string,
  oa: string,
  data_directories: string,
  date: string
}
//@ts-ignore
dayjs.extend(utc)
dayjs.extend(timezone)

const IS_PRODUCTION: boolean = process.env.REACT_APP_ENVIRONMENT === 'production'
const barHeight = '60px';
const drawerWidth = '400px';
export const useStyles = makeStyles((theme: unknown) => ({
  root: {
    display: "flex",
    position: 'relative',
    height: '1000px'
  },
  toolbar: {
    paddingRight: '12px',
    paddingLeft: '20px'
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
  },
  appBar: {
    position: 'fixed',
    height: barHeight,
    width: '100%',
  },
  leftDrawer: {
    overflow: 'auto',
    width: drawerWidth,
  },
  drawerPaper: {
    marginTop: barHeight,
    position: 'absolute',
    width: drawerWidth,
  },
  menuButton: {
    marginRight: '16px'
  },
  title: {
    marginLeft: '8px',
    flexGrow: 1,
  },
  content: {
    flexGrow: 1,
    marginTop: barHeight,
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: MAX_WIDTH
  },
  contentShift: {
    marginTop: barHeight,
    marginLeft: 0
  },
  container: {
    paddingTop: '36px',
    paddingBottom: '16px'
  },
  fixedHeight: { height: 240 },
  form: {
    margin: '0px',
    padding: '0px'
  }
}));


declare module '@mui/material/styles' {
  interface Components {
    [key: string]: any
  }
}

export interface Levels {
  lev0: boolean,
  lev1: boolean,
  lev2: boolean,
}

export interface DownloadFilenames {
  ofname: boolean,
  koaid: boolean
}

export interface InstUserSem {
  userid?: string | null,
  setUserid: Function,
  semid?: string | null
  setSemid?: Function,
  imgSrc?: string
  setImgSrc: Function,
  levels: Levels,
  setLevels: Function,
  instruments: string[],
  downloadFilenames: DownloadFilenames,
  setDownloadFilenames: Function
}

const initISU: InstUserSem = {
  userid: '',
  setUserid: () => { },
  semid: '',
  setSemid: () => { },
  imgSrc: '',
  setImgSrc: () => { },
  levels: { lev0: true, lev1: true, lev2: true },
  setLevels: () => { },
  instruments: [],
  downloadFilenames: { koaid: true, ofname: false },
  setDownloadFilenames: () => { }
}

const ISUContext = createContext<InstUserSem>(initISU as InstUserSem)
export const useISUContext = () => useContext(ISUContext)

const handleTheme = (darkState: boolean | null | undefined): Theme => {
  const theme = createTheme({

    components: {
      Dashboard: {
        root: {
          '&': {
            backgroundColor: '#FF040D'
          }
        }
      },
      MUIDataTableBodyRow: {
        root: {
          '&:nth-child(odd)': {
            backgroundColor: '#FF0000'
          },

        }
      },
      MUIDataTableCell: {
        styleOverrides: {
          root: {
            padding: '0px',
            borderBottom: [2, 'solid', '#666666'],
          }
        }
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          root: {
            // borderRight: [[2, 'solid', '#666666']]
          },
        },
      },
    },
    palette: {
      mode: darkState ? 'dark' : 'light',
    }
  })
  return theme
}

interface LocationState {
  from: {
    pathname: string
  }
}


function App() {
  const classes = useStyles();
  const [darkState, setDarkState] = useQueryParam('darkState', withDefault(BooleanParam, true));
  const [toggleDrawer, setToggle] = useQueryParam('sidebarToggle', withDefault(BooleanParam, false));
  const [levels, setLevels] = useQueryParam('setLevels', withDefault(JsonParam, {
    lev0: false, lev1: false, lev2: false
  }));
  const [downloadFilenames, setDownloadFilenames] = useState();
  const theme = handleTheme(darkState)

  // TOX
  const { loggedIn, user, token, isAuthenticated, storesession, watchDate, refreshtoken, updateRefreshToken, setWatchDate, get_localsession, startnewsession, clearsession } = useAuth();
  const [session_elapsed, setSessionElapsed] = useState(false);
  const [tokenWatchDate, setTokenWatchDate] = useState('19910101');

  const now_ = dayjs().utcOffset(0);

  const [USER_RUN_MODE, setUSER_RUN_MODE] = useState(0)
  // Run Modes - 0 - Production, 1 - Maintaineance, 2 - Test

  const SESSION_DATE = USER_RUN_MODE == 1 ? "2023-08-28" : now_.format('YYYY-MM-DD');
  //const SESSION_DATE = "2023-08-22" ;

  const history = useHistory();
  localStorage.end_session = 0;


  useEffect(() => {
    try {
      if (token) {
        const result: any = jwt_decode(token);
        if (result != "") {
          const _obs = result.obs_dt ? result.obs_dt : ""
          setTokenWatchDate( _obs ) 
        }
      }
    } catch (error) {
      console.log('Err:', error);
    }

  }, [token])

  useEffect(() => {
    // Set Run Mode
    setUSER_RUN_MODE(0);
    setWatchDate(SESSION_DATE)

    const session_limit_timer = setInterval(() => {

      const __ = tokenWatchDate ? tokenWatchDate != "" : ""

      const end_watch_date = dayjs.tz(`${tokenWatchDate} 00:00`, 'GMT').add(1, 'day')
      const token_end_watch_date = dayjs.tz(`${tokenWatchDate} 00:00`, 'GMT').add(1, 'day')
      const current_date = dayjs().utcOffset(0); //.subtract(1, 'day').set('hour', 0).set('minute', 0).set('second', 0)

      if (localStorage.session_override != 1) {

	      if ((current_date > token_end_watch_date) && loggedIn == true) {
		setSessionElapsed(true);
	      }else {
		setSessionElapsed(false);
              }
      }else { 
          setSessionElapsed(true);
      }

    }, 5000)

    

    return () => {
      clearInterval(session_limit_timer)
    }
  }, [loggedIn, watchDate, tokenWatchDate]);

  useEffect(()=>{
          if (tokenWatchDate != "19910101")
          setTimeout( () => {
                const wdate_str = `${tokenWatchDate.substr(0,4)}/${tokenWatchDate.substr(4,2)}/${tokenWatchDate.substr(6,2)}`;
                const SessionElapsedNotice = ({}) => {
                      return (<div className="session_end_prompt"><p style={{ height: "70%", backgroundColor: "#e74c3c" }}>The portal is intended for use on a single observing night. Once your current observing session has ended  you will no longer see new data added to the table for (<strong>{wdate_str}</strong>). Stay logged in to examine all the night’s data and to download the files. If you are ready for the next night’s observing, logout and then log back in again. If you have finished observing, simply log off. </p></div>)
                }

	        if ( (session_elapsed && loggedIn ) ) {
	            toast(<SessionElapsedNotice />, {
                      toastId: 'session_notice',
	              position: "bottom-center",
	              draggable: false,
	              theme: "dark",
	              className: "session_end_prompt",
                      autoClose: false,
                    });
	        }
          }, 2500 );
  }, [session_elapsed])

  const handleThemeChange = () => {
    setDarkState(!darkState)
  }

  return (
    <Router basename='/rti-gui'>
      {(USER_RUN_MODE > 0) ?
        <div style={{ backgroundColor: 'red', color: '#FFF', fontWeight: 'bold', fontSize: '12px', textAlign: 'center', borderBottom: '1px solid', marginBottom: '1em' }}>
          <h1 style={{ margin: 0, padding: 0, lineHeight: '1em' }}> MAINTENANCE IN PROGRESS</h1><p style={{ margin: 0, padding: 0, lineHeight: '1em' }}>Expect the unexpected or Check back later</p>
        </div> :
        <></>
      }
      <ThemeProvider theme={theme}>
        <Banner />
        {
          loggedIn ?
            <Switch>
              <Route exact path="/profile" component={Profile} />
              <Route exact path="/dashboard" component={Dashboard} />
              <Route path="*" component={Gui}></Route>
            </Switch>
            :
            <Login history={history} />
        }
        <ToastContainer style={{ width: "800px", maxWidth: "80vw" }} ></ToastContainer>
        <FooterBar />
        <br/>
        <br/>
        <br/>
        <Container sx={{ textAlign: 'center', marginTop: '10em' }} fixed>
            <VersionInfo />
        </Container>
      </ThemeProvider>
    </Router>
  )
}
export default App;
